// src/components/dashboard/CampaignTable.tsx
//
// During a bulk run you almost never want to look at one email. You want to know
// whether run #4471 has finished, and how much of it bounced. Campaigns are
// grouped on the part of ClientReference before the first colon, so a sender
// that stamps "run-4471:person-88" gets this for free.

import React from 'react';
import { Layers } from 'lucide-react';
import { CampaignSummary } from '../../types/stage';

interface CampaignTableProps {
  campaigns: CampaignSummary[];
  isLoading?: boolean;
  onDrillIn: (campaign: string) => void;
}

interface Segment {
  key: keyof CampaignSummary;
  label: string;
  bar: string;
  text: string;
}

const SEGMENTS: Segment[] = [
  { key: 'delivered', label: 'Delivered', bar: 'bg-green-500', text: 'text-green-700' },
  { key: 'inTransit', label: 'Sent', bar: 'bg-blue-400', text: 'text-blue-700' },
  { key: 'sending', label: 'Sending', bar: 'bg-blue-600', text: 'text-blue-700' },
  { key: 'queued', label: 'Queued', bar: 'bg-gray-400', text: 'text-gray-700' },
  { key: 'bounced', label: 'Bounced', bar: 'bg-red-500', text: 'text-red-700' },
  { key: 'failed', label: 'Failed', bar: 'bg-red-700', text: 'text-red-800' },
  { key: 'cancelled', label: 'Cancelled', bar: 'bg-gray-300', text: 'text-gray-600' },
  { key: 'archived', label: 'Archived', bar: 'bg-gray-200', text: 'text-gray-500' },
];

const CampaignTable: React.FC<CampaignTableProps> = ({ campaigns, isLoading, onDrillIn }) => {
  if (isLoading) {
    return (
      <div className="p-6 space-y-3">
        {[0, 1, 2].map((i) => (
          <div key={i} className="h-16 bg-gray-100 rounded animate-pulse" />
        ))}
      </div>
    );
  }

  if (campaigns.length === 0) {
    return (
      <div className="text-center py-16">
        <Layers className="mx-auto h-10 w-10 text-gray-300" />
        <h3 className="mt-3 text-sm font-medium text-gray-900">No campaigns yet</h3>
        <p className="mt-1 text-sm text-gray-500 max-w-md mx-auto">
          Campaigns appear when submitted emails carry a client reference. Use a shared
          prefix followed by a colon — <code className="text-xs bg-gray-100 px-1 rounded">run-4471:person-88</code> —
          and each run is grouped here.
        </p>
      </div>
    );
  }

  return (
    <div className="divide-y divide-gray-200">
      {campaigns.map((c) => {
        const finished = c.delivered + c.bounced + c.failed + c.cancelled + c.archived;
        const percent = c.total > 0 ? Math.round((finished / c.total) * 100) : 0;

        return (
          <div key={c.campaign} className="p-4 hover:bg-gray-50">
            <div className="flex flex-wrap items-baseline justify-between gap-2 mb-2">
              <button
                onClick={() => onDrillIn(c.campaign)}
                className="text-sm font-semibold text-blue-700 hover:text-blue-900 hover:underline"
              >
                {c.campaign}
              </button>
              <div className="text-xs text-gray-500 tabular-nums">
                {finished.toLocaleString()} of {c.total.toLocaleString()} finished ({percent}%)
                {' · started '}
                {new Date(c.firstQueuedAt).toLocaleString()}
              </div>
            </div>

            {/* One bar, proportional. Easier to read at a glance than eight numbers. */}
            <div className="flex h-2 w-full overflow-hidden rounded-full bg-gray-100 mb-2">
              {SEGMENTS.map((s) => {
                const value = c[s.key] as number;
                if (!value) return null;
                return (
                  <div
                    key={s.key}
                    className={s.bar}
                    style={{ width: `${(value / c.total) * 100}%` }}
                    title={`${s.label}: ${value.toLocaleString()}`}
                  />
                );
              })}
            </div>

            <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs tabular-nums">
              {SEGMENTS.map((s) => {
                const value = c[s.key] as number;
                if (!value) return null;
                return (
                  <span key={s.key} className={s.text}>
                    <span className={`inline-block w-2 h-2 rounded-sm mr-1 ${s.bar}`} />
                    {s.label} {value.toLocaleString()}
                  </span>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default CampaignTable;
